import { get, post, put, del } from '../lib/apiClient';

/**
 * Organization websites (integration/openapi.yaml, tag Sites;
 * docs/Decisions.md D43). Every call except getPublicSite acts on the active
 * org (X-Org-Id is added by the API client).
 */

/** GET /sites/public/{slug} - no auth */
export const getPublicSite = slug =>
  get(`/sites/public/${encodeURIComponent(slug)}`, { auth: false });

/** GET /sites/config */
export const getSiteConfig = () => get('/sites/config');

/** PUT /sites/config - any subset of template, pattern, palette, siteTitle, tagline, pages */
export const updateSiteConfig = changes => put('/sites/config', changes);

/** POST /sites/config/publish */
export const setSitePublished = published => post('/sites/config/publish', { published });

/** GET /sites/groups */
export const listSiteGroups = () => get('/sites/groups');

/** PUT /sites/editors */
export const setSiteEditors = groupIds => put('/sites/editors', { groupIds });

/** POST /sites/logo/upload-request */
export const requestLogoUpload = file =>
  post('/sites/logo/upload-request', {
    fileName: file.name,
    contentType: file.type,
    size: file.size,
  });

/** POST /sites/logo/confirm */
export const confirmLogo = logoId => post('/sites/logo/confirm', { logoId });

/** DELETE /sites/logo */
export const removeLogo = () => del('/sites/logo');

/** GET /sites/uploads */
export const listSiteUploads = () => get('/sites/uploads');

/** POST /sites/uploads */
export const requestSiteUpload = (file, mode) =>
  post('/sites/uploads', { fileName: file.name, size: file.size, mode });

/** GET /sites/uploads/{uploadId} */
export const getSiteUpload = uploadId => get(`/sites/uploads/${encodeURIComponent(uploadId)}`);

/** POST /sites/uploads/{uploadId}/scan */
export const scanSiteUpload = uploadId =>
  post(`/sites/uploads/${encodeURIComponent(uploadId)}/scan`);

/** POST /sites/uploads/{uploadId}/publish (publishing an earlier version is the rollback) */
export const publishSiteUpload = uploadId =>
  post(`/sites/uploads/${encodeURIComponent(uploadId)}/publish`);

/** DELETE /sites/uploads/{uploadId} */
export const deleteSiteUpload = uploadId => del(`/sites/uploads/${encodeURIComponent(uploadId)}`);

/** DELETE /sites/hosted */
export const takeHostedSiteOffline = () => del('/sites/hosted');

/**
 * PUT a file to a presigned URL from requestLogoUpload / requestSiteUpload.
 * Goes straight to S3, so it does not use the API client (no envelope, no
 * bearer token). The Content-Type must match what was signed.
 */
export async function putToPresignedUrl(url, file, contentType) {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(
      `The upload failed (HTTP ${response.status}). Try again; the link expires after 15 minutes.`
    );
  }
}

// Custom domains for the uploaded site (docs/Decisions.md D45). Org admins and
// platform admins, premium site-upload.

/** GET /sites/domains */
export const listSiteDomains = () => get('/sites/domains');

/** POST /sites/domains */
export const addSiteDomain = domain => post('/sites/domains', { domain });

/** POST /sites/domains/{domain}/verify - checks the TXT record */
export const verifySiteDomain = domain =>
  post(`/sites/domains/${encodeURIComponent(domain)}/verify`);

/** POST /sites/domains/{domain}/refresh - certificate and DNS status */
export const refreshSiteDomain = domain =>
  post(`/sites/domains/${encodeURIComponent(domain)}/refresh`);

/** DELETE /sites/domains/{domain} */
export const removeSiteDomain = domain => del(`/sites/domains/${encodeURIComponent(domain)}`);
